import { eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@stockpilot/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return "success"**. Measured in `space-academy-app` (same
 * canon, `activity` table): course created, 1.03 s later unit is created, and
 * in between NO `course/updated` op exists — the rename that the author had
 * just written never generated a CRUD operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the local
 * view from the last checkpoint**. If the checkpoint does not yet bring the
 * newly created row, the row is **ABSENT during that instant** — and an
 * UPDATE against it writes nothing, generates no op, and **does not fail**. The
 * window is not "X ms passed": it is "the queue emptied in between", so it cannot
 * be detected after the fact with a fixed timeout.
 *
 * Why absence of error proves nothing here: the server write-path returns
 * **2xx always by design** (a 4xx would block the client queue forever). A user
 * write that vanishes without a trace is the worst possible failure mode — in
 * Stockpilot, a stock count or adjustment that the user thinks they saved
 * imbalances inventory without leaving a trace.
 *
 * Contract of `patchRowVerified`/`waitForRowPresent`: write, **REREAD**,
 * compare. If the value did not land, retry with backoff; if upon exhaustion
 * it still hasn't landed, **THROW** — the caller reports it instead of leaving
 * the user looking at a field that represents nothing saved.
 *
 * Canon: `listto-app` `9ae347b6bf906c4ba53ea98a5a33ece5e9be0e44`.
 *
 * ── Second class: upsert that decides INSERT-vs-UPDATE with its own SELECT ──
 *
 * `stock_quant` (the on-hand derived from product×location×lot) has no identity
 * known in advance: `adjustStockMutation`, `confirmPickingMutation` and the rest
 * of the mutations in `../inventory/mutations.ts` decide whether to INSERT a new
 * row or UPDATE the existing one with their own SELECT. That SELECT falls into
 * the SAME window: if the quant exists but just reappeared after a checkpoint
 * rebuild, the SELECT sees it absent and the code INSERTs a new row — two quants
 * for the same product×location×lot, imbalanced on-hand without any visible error.
 * `readMatchingRowVerified` covers this class: retries the deciding read with the
 * same backoff before the caller concludes "does not exist yet, must create it".
 *
 * Unlike `patchRowVerified`, here there is NO error to throw upon exhausting
 * the backoff: genuine absence (first time that product×location is touched) is a
 * valid result, so `readMatchingRowVerified` simply returns `undefined` and the
 * caller proceeds to INSERT, exactly as before. The backoff only shrinks the false
 * negative window; it cannot eliminate it completely without another signal (the
 * CRUD queue itself).
 *
 * ── What this module does NOT cover (deliberate) ────────────────────────────
 * Hard DELETEs are not verified: their natural check would be "the row is no
 * longer there", and "absent" is precisely the ambiguous state of this window —
 * one cannot distinguish "was deleted" from "had not arrived yet" without another
 * signal.
 */

/**
 * Retry backoff. Sums ≈ 6.05 s: more than covers the round trip of a
 * checkpoint (measured at ~1 s against the local stack) without leaving a fast
 * interaction hanging forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Delays between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error of a local-first write that failed to land on the row. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read is by
  // primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, rereads, compares the patch
 * fields and retries as long as they have not landed. Throws `LocalWriteLostError`
 * if they are still missing upon exhausting the backoff.
 *
 * `updated_at` is set by this function on each attempt (and is not compared: it
 * changes on every pass); `patch` must bring only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for the row to EXIST in the local view before touching it.
 *
 * For mutations that are not a simple patch — a transaction that confirms a
 * document (picking, purchase order, count, manufacturing order, landed cost,
 * return) by its already known `id`. There, retrying INSIDE the transaction is
 * invalid (it would sleep with the write lock held): wait BEFORE opening it.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Rereads, with the same backoff, a row identified by CONTENT (not by `id`) — the
 * case of `stock_quant`, whose key is product×location×lot, not an id the caller
 * already knows.
 *
 * ALWAYS called before opening the transaction that decides INSERT-vs-UPDATE
 * (same reason as `waitForRowPresent`: do not sleep with the lock held). Unlike
 * `waitForRowPresent`, exhausting the backoff is NOT an error: the row might
 * truly not exist yet (first time that product×location is touched), and that is
 * a valid result — caller proceeds to INSERT.
 *
 * `read` must repeat the SAME query on each attempt (a closure over `db`/`trx`
 * and conditions, not an already resolved promise).
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}
